import { FC, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  Boxes, RefreshCw, AlertTriangle, ChevronDown, ChevronRight,
  Search, TrendingUp, Package, DollarSign,
  ShoppingCart, Zap, BarChart3, Target, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import { IStoreResponse } from '../interfaces/responses/StoreResponse.interface';
import './StockIntelligence.page.css';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SkuBreakdown {
  _id: string;
  Sku: string;
  name: string;
  imgUri: string;
  url: string;
  storeId: string;
  storeName: string;
  delivered7d: number;
  totalOrders7d: number;
  delivered30d: number;
  totalOrders30d: number;
  revenue7d: number;
  revenue30d: number;
}

interface StockGroup {
  _id: string;
  name: string;
  buyingRate: number;
  transportFees: number;
  storageFees: number;
  packageFees: number;
  stockAvailable: number;
  totalSkus: number;
  skus: SkuBreakdown[];
  delivered7d: number;
  canceled7d: number;
  returned7d: number;
  failed7d: number;
  pending7d: number;
  shipped7d: number;
  readyToShip7d: number;
  totalOrders7d: number;
  revenue7d: number;
  delivered7dPrev: number;
  totalOrders7dPrev: number;
  revenue7dPrev: number;
  delivered30d: number;
  canceled30d: number;
  returned30d: number;
  failed30d: number;
  pending30d: number;
  shipped30d: number;
  readyToShip30d: number;
  totalOrders30d: number;
  revenue30d: number;
  salesVelocity7d: number;
  salesVelocity30d: number;
  stockCoverageDays: number;
  netProfit7d: number;
  netProfit30d: number;
  margin30d: number;
  returnRate30d: number;
  trend7d: number;
}

type DecisionKey = 'winner' | 'stock-risk' | 'stock-warn' | 'dead-stock' | 'near-dead' |
  'price-up' | 'price-down' | 'stop-buying' | 'test-more' | 'watch';

interface Decision {
  key: DecisionKey;
  emoji: string;
  label: string;
  reason: string;
  confidence: 'High' | 'Medium' | 'Low';
  suggestedAction: string;
  colorClass: string;
}

interface ReorderRec {
  reorderQty: number;
  daysLeft: number;
  urgency: 'critical' | 'high' | 'medium' | 'none';
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMAD = (n: number) => `${Math.round(n).toLocaleString('en-US')} MAD`;
const fmtPct = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
const clamp  = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const fmt1   = (n: number) => n.toFixed(1);

// ─── AI Decision Engine ───────────────────────────────────────────────────────
const getDecision = (g: StockGroup): Decision => {
  const vel      = g.salesVelocity7d;
  const coverage = g.stockCoverageDays;
  const margin   = g.margin30d;
  const trend    = g.trend7d;
  const rr       = g.returnRate30d;
  const orders30 = g.totalOrders30d;
  const stock    = g.stockAvailable;

  // 🔥 WINNER
  if (vel >= 3 && trend > 10 && margin >= 20 && rr < 30) {
    return {
      key: 'winner', emoji: '🔥', label: 'WINNER – Scale Now',
      reason: `${fmt1(vel)} orders/day · +${fmt1(trend)}% trend · ${fmt1(margin)}% margin`,
      confidence: vel >= 5 ? 'High' : 'Medium',
      suggestedAction: 'Maximize stock. Activate Jumia Express. Do not raise price — protect velocity.',
      colorClass: 'si__decision--green',
    };
  }

  // ⚠️ CRITICAL STOCK RISK (< 3 days)
  if (coverage < 3 && vel > 0.3) {
    return {
      key: 'stock-risk', emoji: '⚠️', label: 'Reorder NOW',
      reason: `Only ${fmt1(coverage)}d of stock at ${fmt1(vel)} orders/day`,
      confidence: 'High',
      suggestedAction: `Reorder ${Math.ceil(vel * 30)} units immediately to avoid stockout.`,
      colorClass: 'si__decision--red',
    };
  }

  // ⚠️ STOCK WARNING (3-7 days)
  if (coverage < 7 && coverage >= 3 && vel > 0.3) {
    return {
      key: 'stock-warn', emoji: '⚠️', label: 'Reorder Soon',
      reason: `${fmt1(coverage)}d of stock remaining at ${fmt1(vel)} orders/day`,
      confidence: 'High',
      suggestedAction: `Reorder ${Math.ceil(vel * 20)} units now. 20-day safety stock.`,
      colorClass: 'si__decision--orange',
    };
  }

  // ⛔ DEAD STOCK (0 orders in 30d, has stock)
  if (stock > 10 && orders30 === 0) {
    return {
      key: 'dead-stock', emoji: '⛔', label: 'Dead Stock – Liquidate',
      reason: `${stock} units in stock, 0 orders in 30 days`,
      confidence: 'High',
      suggestedAction: 'Discount 20-35%. Stop all reorders. Try bundle deals or push to other channels.',
      colorClass: 'si__decision--red',
    };
  }

  // ⛔ NEAR-DEAD (< 3 orders 30d, has stock)
  if (stock > 5 && orders30 < 3 && vel < 0.2) {
    return {
      key: 'near-dead', emoji: '⛔', label: 'Near-Dead Stock',
      reason: `Only ${orders30} orders in 30 days with ${stock} units in stock`,
      confidence: 'Medium',
      suggestedAction: 'Drop price 15-25% to stimulate demand. Monitor for 7 days. Do not reorder.',
      colorClass: 'si__decision--orange',
    };
  }

  // 💰 PRICE UP opportunity
  if (vel >= 1.5 && margin >= 40 && rr < 20) {
    return {
      key: 'price-up', emoji: '💰', label: 'Increase Price',
      reason: `${fmt1(margin)}% margin with ${fmt1(vel)} orders/day — room to grow`,
      confidence: 'Medium',
      suggestedAction: 'Test +5-15 MAD price increase. Monitor for 3 days.',
      colorClass: 'si__decision--green',
    };
  }

  // ❌ STOP BUYING
  if (orders30 < 2 && stock < 5) {
    return {
      key: 'stop-buying', emoji: '❌', label: 'Stop Buying',
      reason: `Only ${orders30} orders in 30 days — not worth restocking`,
      confidence: 'Medium',
      suggestedAction: 'Do not reorder. Sell remaining stock at any price to recover cash.',
      colorClass: 'si__decision--gray',
    };
  }

  // 🔍 TEST MORE SKUs
  if (g.totalSkus <= 2 && vel >= 0.5 && trend > 20) {
    return {
      key: 'test-more', emoji: '🔍', label: 'Test More SKUs',
      reason: `+${fmt1(trend)}% trend with only ${g.totalSkus} SKU(s) — untapped potential`,
      confidence: 'Medium',
      suggestedAction: 'Add 2-3 more SKUs with different price points and listings to maximize reach.',
      colorClass: 'si__decision--blue',
    };
  }

  // 📉 PRICE DOWN
  if (rr > 30 || (vel < 0.3 && orders30 > 2)) {
    return {
      key: 'price-down', emoji: '📉', label: 'Reduce Price',
      reason: rr > 30
        ? `${fmt1(rr)}% return rate — buyers unsatisfied or expectations too high`
        : `Low velocity (${fmt1(vel)}/day) despite having orders`,
      confidence: 'Medium',
      suggestedAction: 'Test 10-20 MAD price drop. Check competitor pricing on Jumia.',
      colorClass: 'si__decision--yellow',
    };
  }

  // 🔥 EMERGING WINNER
  if (vel >= 1 && trend >= 40) {
    return {
      key: 'winner', emoji: '🚀', label: 'Accelerating – Watch Close',
      reason: `+${fmt1(trend)}% trend with ${fmt1(vel)} orders/day — gaining momentum`,
      confidence: 'Medium',
      suggestedAction: 'Build stock now before velocity peaks. Activate Jumia Express.',
      colorClass: 'si__decision--green',
    };
  }

  // 👁 WATCH
  return {
    key: 'watch', emoji: '👁', label: 'Monitor',
    reason: `${fmt1(vel)} orders/day · ${coverage > 999 ? 'no recent sales' : fmt1(coverage) + 'd stock'}`,
    confidence: 'Low',
    suggestedAction: 'No immediate action needed. Check again in 7 days.',
    colorClass: 'si__decision--blue',
  };
};

// ─── Reorder Recommendation ───────────────────────────────────────────────────
const getReorder = (g: StockGroup): ReorderRec | null => {
  const vel = g.salesVelocity7d;
  if (vel <= 0) return null;
  const target = Math.ceil(vel * 30);
  const reorderQty = target - g.stockAvailable;
  if (reorderQty <= 0) return null;

  const days = g.stockCoverageDays;
  const urgency: ReorderRec['urgency'] =
    days < 3 ? 'critical' : days < 7 ? 'high' : days < 14 ? 'medium' : 'none';
  if (urgency === 'none') return null;

  return {
    reorderQty,
    daysLeft: days,
    urgency,
    message:
      urgency === 'critical'
        ? `Reorder ${reorderQty} units NOW — stockout in ${fmt1(days)} days`
        : urgency === 'high'
        ? `Reorder ${reorderQty} units soon — only ${fmt1(days)} days of stock`
        : `Plan reorder of ${reorderQty} units — ${fmt1(days)} days remaining`,
  };
};

// ─── Reorder Decision Guide (rule-based, no API) ─────────────────────────────
interface ReorderGuide {
  urgency: 'critical' | 'high' | 'medium' | 'low';
  stockStrategy: 'bulk' | 'minimum' | 'skip';
  reorderQty: number;
  targetBuyingPrice: number;
  savingsPerUnit: number;
  suggestedSellingPrice: number;
  expectedMargin: number | null;
  coverageDays: number;
  reasoning: string;
  risks: string[];
  tips: string[];
}

const getReorderGuide = (g: StockGroup): ReorderGuide => {
  const vel       = g.salesVelocity7d;
  const coverage  = g.stockCoverageDays;
  const margin    = g.margin30d;
  const trend     = g.trend7d;
  const rr        = g.returnRate30d;
  const stock     = g.stockAvailable;
  const orders30  = g.totalOrders30d;
  const totalCost = g.buyingRate + g.transportFees + g.storageFees + g.packageFees;
  const avgSell   = g.delivered30d > 0 ? Math.round(g.revenue30d / g.delivered30d) : 0;

  // ── Urgency ────────────────────────────────────────────────────────────────
  const urgency: ReorderGuide['urgency'] =
    vel > 0.3 && coverage < 3  ? 'critical' :
    vel > 0.3 && coverage < 7  ? 'high'     :
    vel > 0.3 && coverage < 21 ? 'medium'   : 'low';

  // ── Stock strategy ─────────────────────────────────────────────────────────
  const stockStrategy: ReorderGuide['stockStrategy'] =
    vel <= 0 || orders30 < 2                          ? 'skip'    :
    vel >= 3 && trend > 10 && margin >= 20 && rr < 25 ? 'bulk'    :
    vel >= 1 && trend >= 0 && margin >= 15            ? 'bulk'    :
    vel > 0.3                                         ? 'minimum' : 'skip';

  // ── Reorder qty ───────────────────────────────────────────────────────────
  const targetDays  = stockStrategy === 'bulk' ? 45 : 30;
  const targetStock = Math.ceil(vel * targetDays);
  const reorderQty  = stockStrategy === 'skip' ? 0 : Math.max(0, targetStock - stock);

  // ── Pricing suggestions ────────────────────────────────────────────────────
  const targetMargin = 0.30;
  const otherCosts = g.transportFees + g.storageFees + g.packageFees;

  // Suggested sell price: back-calculated to hit 30% margin from current costs
  const suggestedSellingPrice = totalCost > 0 ? Math.ceil(totalCost / (1 - targetMargin)) : avgSell;

  // Target buy price: the MAX price we should pay to achieve 30% margin at current avg sell
  // Must ALWAYS be strictly below the last known buying rate (g.buyingRate)
  const targetFromMargin = avgSell > 0
    ? Math.floor(avgSell * (1 - targetMargin) - otherCosts)
    : Math.floor(g.buyingRate * 0.88); // fallback: 12% below current if no sell data

  // Clamp: never suggest equal to or above last buying rate — always push for a better deal
  const targetBuyingPrice = Math.max(1, Math.min(targetFromMargin, g.buyingRate - 1));
  const savingsPerUnit = Math.max(0, g.buyingRate - targetBuyingPrice);

  // Expected margin using target buy price (not current)
  const totalCostAtTarget = targetBuyingPrice + otherCosts;
  const expectedMargin = suggestedSellingPrice > 0
    ? Math.round(((suggestedSellingPrice - totalCostAtTarget) / suggestedSellingPrice) * 100)
    : null;

  // ── Coverage after reorder ─────────────────────────────────────────────────
  const coverageDays = vel > 0 ? Math.round(reorderQty / vel) : 0;

  // ── Reasoning ─────────────────────────────────────────────────────────────
  const reasonParts: string[] = [];
  if (stockStrategy === 'skip') {
    reasonParts.push(`Only ${orders30} orders in 30 days — not worth restocking at this pace.`);
    reasonParts.push('Sell remaining units at any price to recover cash.');
  } else {
    if (urgency === 'critical') reasonParts.push(`Stock critically low — only ${fmt1(coverage)} days left at ${fmt1(vel)} orders/day.`);
    else if (urgency === 'high') reasonParts.push(`Only ${fmt1(coverage)} days of stock remain — reorder now to avoid stockout.`);
    if (stockStrategy === 'bulk') reasonParts.push(`Strong velocity (${fmt1(vel)}/day) and ${trend > 0 ? '+' : ''}${fmt1(trend)}% trend justify building a 45-day reserve.`);
    else reasonParts.push(`Order ${reorderQty} units to cover 30 days at current velocity.`);
    if (savingsPerUnit > 0) reasonParts.push(`Negotiate buying price from ${g.buyingRate} MAD down to ${targetBuyingPrice} MAD (save ${savingsPerUnit} MAD/unit) to reach ${Math.round(targetMargin * 100)}% margin.`);
    else if (margin >= 30) reasonParts.push(`Good margin (${fmt1(margin)}%) — focus on scaling volume.`);
  }

  // ── Risks ──────────────────────────────────────────────────────────────────
  const risks: string[] = [];
  if (rr > 25) risks.push(`High return rate (${fmt1(rr)}%) — buyers may be dissatisfied with product quality or description.`);
  if (trend < -15) risks.push(`Demand declining ${fmt1(Math.abs(trend))}% vs last week — risk of slow-moving stock after reorder.`);
  if (margin < 10) risks.push(`Margin below 10% — a small price drop or return spike could make this product unprofitable.`);
  if (stockStrategy === 'bulk' && rr > 20) risks.push('Building large stock with a high return rate is risky — fix returns before scaling.');
  if (g.totalSkus <= 1 && vel >= 1) risks.push('Single SKU — any listing issue or ban kills all revenue from this group.');
  if (coverage > 60 && vel < 0.5) risks.push(`Excess stock (${fmt1(coverage)}d) for slow velocity — cash tied up, storage costs accumulating.`);
  if (risks.length === 0) risks.push('No major risks detected — continue monitoring weekly.');

  // ── Tips ───────────────────────────────────────────────────────────────────
  const tips: string[] = [];
  if (stockStrategy === 'skip') {
    tips.push('Discount 20–35% to liquidate remaining stock quickly.');
    tips.push('Do not place any new orders until demand returns.');
  } else {
    if (savingsPerUnit > 0) tips.push(`Negotiate buying price from ${g.buyingRate} MAD → ${targetBuyingPrice} MAD/unit. Saving ${savingsPerUnit} MAD/unit × ${reorderQty} units = ${savingsPerUnit * reorderQty} MAD total savings on this order.`);
    if (avgSell > 0 && suggestedSellingPrice > avgSell) tips.push(`Test raising selling price to ${suggestedSellingPrice} MAD — current avg (${avgSell} MAD) leaves margin on the table.`);
    else if (avgSell > 0 && margin < 15) tips.push(`Consider setting price at ${suggestedSellingPrice} MAD to improve margin to ~30%.`);
    if (g.totalSkus <= 2 && vel >= 1) tips.push('Add 2–3 more SKU variants to maximise reach across price points.');
    if (rr > 20) tips.push('Improve product photos and description to reduce returns — check competitor listings for cues.');
    if (trend > 30) tips.push('Demand is accelerating — activate Jumia Express to capitalise on momentum.');
    if (urgency === 'critical' || urgency === 'high') tips.push('Order immediately — do not wait for the next buying cycle.');
  }
  if (tips.length < 2) tips.push('Review pricing weekly against top competitors on Jumia.');

  return {
    urgency, stockStrategy, reorderQty,
    targetBuyingPrice,
    savingsPerUnit,
    suggestedSellingPrice,
    expectedMargin,
    coverageDays,
    reasoning: reasonParts.join(' '),
    risks,
    tips,
  };
};

// ─── Coverage UI helpers ──────────────────────────────────────────────────────
const covClass = (days: number, hasSales: boolean): string => {
  if (!hasSales) return 'none';
  if (days < 3)  return 'critical';
  if (days < 7)  return 'warning';
  if (days < 30) return 'ok';
  return 'safe';
};

const covPct = (days: number): number =>
  days >= 999 ? 5 : clamp((days / 60) * 100, 2, 100);

// ─── Velocity dots ─────────────────────────────────────────────────────────────
const VelDots: FC<{ vel: number }> = ({ vel }) => {
  const filled = vel >= 5 ? 5 : vel >= 3 ? 4 : vel >= 1.5 ? 3 : vel >= 0.5 ? 2 : vel > 0 ? 1 : 0;
  const high = vel >= 3;
  return (
    <div className="si__vel-dots">
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} className={`si__vel-dot${i < filled ? (high ? ' si__vel-dot--high' : ' si__vel-dot--on') : ''}`} />
      ))}
    </div>
  );
};

// ─── Sort header ──────────────────────────────────────────────────────────────
const SortTh: FC<{
  field: string; label: string;
  sortBy: string; sortDir: 'asc' | 'desc';
  onSort: (f: string) => void;
}> = ({ field, label, sortBy, sortDir, onSort }) => (
  <th
    className={`sortable${sortBy === field ? ' sorted' : ''}`}
    onClick={() => onSort(field)}
  >
    {label}{sortBy === field ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
  </th>
);

// ─── Component ────────────────────────────────────────────────────────────────
const StockIntelligencePage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;
  const isAdmin = contextStore.user?.type === 'admin';

  const [stores,        setStores]        = useState<IStoreResponse[]>([]);
  const [groups,        setGroups]        = useState<StockGroup[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [storeId,       setStoreId]       = useState('0');
  const [expandedId,    setExpandedId]    = useState<string | null>(null);
  const [filterDecision,setFilterDecision]= useState('all');
  const [filterRisk,    setFilterRisk]    = useState('all');
  const [search,        setSearch]        = useState('');
  const [sortBy,        setSortBy]        = useState('velocity');
  const [sortDir,       setSortDir]       = useState<'asc' | 'desc'>('desc');
  const [lastUpdated,   setLastUpdated]   = useState<Date | null>(null);

  // Fetch stores list once
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getStores, {}, token);
      if (resp && !resp.error) setStores(resp);
    })();
  }, [token]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchData = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const data = await dispatch(actions.getStockIntelligence, { storeId }, token);
      if (Array.isArray(data)) {
        setGroups(data as StockGroup[]);
        setLastUpdated(new Date());
      } else setLoadError((data as any)?.error?.message || 'the server did not answer (check the connection)');
    } catch (e: any) {
      setLoadError(e?.message || 'network error');
    } finally {
      setLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Sort handler ───────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  // ── Global KPIs ────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalStockValue = groups.reduce((s, g) => s + g.stockAvailable * g.buyingRate, 0);
    const deadStockValue  = groups
      .filter(g => g.totalOrders30d === 0 && g.stockAvailable > 0)
      .reduce((s, g) => s + g.stockAvailable * g.buyingRate, 0);
    const atRiskCount    = groups.filter(g => g.stockCoverageDays < 7 && g.salesVelocity7d > 0.3).length;
    const totalRevenue30 = groups.reduce((s, g) => s + g.revenue30d, 0);
    const totalProfit30  = groups.reduce((s, g) => s + g.netProfit30d, 0);
    const fastMoving     = groups.filter(g => g.salesVelocity7d >= 2).length;
    const deadStockCount = groups.filter(g => g.totalOrders30d === 0 && g.stockAvailable > 0).length;
    const winnerCount    = groups.filter(g => getDecision(g).key === 'winner').length;
    return {
      totalStockValue, deadStockValue, atRiskCount,
      totalRevenue30, totalProfit30, fastMoving, deadStockCount, winnerCount,
    };
  }, [groups]);

  // ── Critical alerts (coverage < 3d) ───────────────────────────────────────
  const criticalAlerts = useMemo(
    () => groups
      .filter(g => g.stockCoverageDays < 3 && g.salesVelocity7d > 0.3)
      .sort((a, b) => a.stockCoverageDays - b.stockCoverageDays)
      .slice(0, 6),
    [groups]
  );

  // ── Warnings (3-7d coverage) ───────────────────────────────────────────────
  const warnAlerts = useMemo(
    () => groups
      .filter(g => g.stockCoverageDays >= 3 && g.stockCoverageDays < 7 && g.salesVelocity7d > 0.3)
      .slice(0, 8),
    [groups]
  );

  // ── Opportunity detection ──────────────────────────────────────────────────
  const opportunities = useMemo(
    () => groups
      .filter(g => g.salesVelocity7d >= 0.5 && (g.trend7d >= 30 || g.margin30d >= 40))
      .sort((a, b) => b.trend7d - a.trend7d)
      .slice(0, 6),
    [groups]
  );

  // ── Dead stock ─────────────────────────────────────────────────────────────
  const deadStock = useMemo(
    () => groups
      .filter(g => g.totalOrders30d === 0 && g.stockAvailable > 0)
      .sort((a, b) => (b.stockAvailable * b.buyingRate) - (a.stockAvailable * a.buyingRate))
      .slice(0, 6),
    [groups]
  );

  // ── Filtered + sorted groups ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = [...groups];

    if (search) {
      const q = search.toLowerCase();
      r = r.filter(g => (g.name || '').toLowerCase().includes(q));
    }

    if (filterDecision !== 'all')
      r = r.filter(g => getDecision(g).key === filterDecision);

    if (filterRisk === 'critical')
      r = r.filter(g => g.stockCoverageDays < 3 && g.salesVelocity7d > 0);
    else if (filterRisk === 'warning')
      r = r.filter(g => g.stockCoverageDays < 7 && g.salesVelocity7d > 0);
    else if (filterRisk === 'safe')
      r = r.filter(g => g.stockCoverageDays >= 14 || g.salesVelocity7d === 0);

    r.sort((a, b) => {
      let av = 0, bv = 0;
      switch (sortBy) {
        case 'velocity': av = a.salesVelocity7d; bv = b.salesVelocity7d; break;
        case 'revenue':  av = a.revenue30d;       bv = b.revenue30d;       break;
        case 'profit':   av = a.netProfit30d;     bv = b.netProfit30d;     break;
        case 'coverage': av = a.stockCoverageDays;bv = b.stockCoverageDays;break;
        case 'trend':    av = a.trend7d;           bv = b.trend7d;           break;
        case 'margin':   av = a.margin30d;         bv = b.margin30d;         break;
        case 'stock':    av = a.stockAvailable;    bv = b.stockAvailable;    break;
        case 'orders30': av = a.totalOrders30d;    bv = b.totalOrders30d;    break;
        default:         av = a.salesVelocity7d;   bv = b.salesVelocity7d;
      }
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return r;
  }, [groups, search, filterDecision, filterRisk, sortBy, sortDir]);

  // ── Skeleton row ───────────────────────────────────────────────────────────
  const SkeletonRow = () => (
    <tr>
      {[200, 90, 70, 80, 80, 70, 60, 120, 80].map((w, i) => (
        <td key={i} className="si__skel-row">
          <div className="si__skel" style={{ width: w }} />
        </td>
      ))}
    </tr>
  );

  // ── SKU Panel ──────────────────────────────────────────────────────────────
  const SkuPanel: FC<{ g: StockGroup }> = ({ g }) => {
    const decision = getDecision(g);
    const reorder  = getReorder(g);
    const topSku   = g.skus.length > 0
      ? [...g.skus].sort((a, b) => b.totalOrders30d - a.totalOrders30d)[0]
      : null;
    const groupTotal30 = g.skus.reduce((s, s2) => s + s2.totalOrders30d, 0);

    const guide = getReorderGuide(g);

    return (
      <div className="si__sku-panel">
        <div className="si__sku-panel-grid">
          {/* Decision detail */}
          <div className="si__decision-card">
            <div className="si__decision-card-title">AI Decision</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span className={`si__decision ${decision.colorClass}`}>
                {decision.emoji} {decision.label}
              </span>
              <span className={`si__confidence si__confidence--${decision.confidence.toLowerCase()}`}>
                {decision.confidence}
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.5rem' }}>
              {decision.reason}
            </div>
            <div className="si__decision-action">→ {decision.suggestedAction}</div>
            <div className="si__decision-meta">
              {g.salesVelocity7d > 0 && (
                <span style={{ fontSize: '0.7rem', background: '#F3F4F6', padding: '0.1rem 0.4rem', borderRadius: 4, color: '#374151' }}>
                  {fmt1(g.salesVelocity7d)} orders/day
                </span>
              )}
              {isAdmin && g.margin30d > 0 && (
                <span style={{ fontSize: '0.7rem', background: '#F3F4F6', padding: '0.1rem 0.4rem', borderRadius: 4, color: '#374151' }}>
                  {fmt1(g.margin30d)}% margin
                </span>
              )}
              {g.returnRate30d > 0 && (
                <span style={{ fontSize: '0.7rem', background: '#FEF2F2', padding: '0.1rem 0.4rem', borderRadius: 4, color: '#991B1B' }}>
                  {fmt1(g.returnRate30d)}% returns
                </span>
              )}
            </div>
          </div>

          {/* Reorder card */}
          <div className="si__reorder-card">
            <div className="si__reorder-title">Smart Reorder</div>
            {reorder ? (
              <>
                <div className="si__reorder-msg">{reorder.message}</div>
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6B7280' }}>Current stock</span>
                    <span style={{ fontWeight: 600 }}>{g.stockAvailable} units</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6B7280' }}>Daily velocity</span>
                    <span style={{ fontWeight: 600 }}>{fmt1(g.salesVelocity7d)} units/day</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6B7280' }}>Reorder qty (30d)</span>
                    <span style={{ fontWeight: 600, color: '#E24B4A' }}>{reorder.reorderQty} units</span>
                  </div>
                  {isAdmin && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                      <span style={{ color: '#6B7280' }}>Cost of reorder</span>
                      <span style={{ fontWeight: 600 }}>{fmtMAD(reorder.reorderQty * g.buyingRate)}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="si__reorder-safe">
                ✓ Stock level OK ({g.stockAvailable} units · {g.stockCoverageDays > 999 ? 'no sales' : fmt1(g.stockCoverageDays) + 'd coverage'})
              </div>
            )}
          </div>
        </div>

        {/* ── Reorder Decision Guide ── */}
        <div className="si__ai-guide-wrap">
          <div className="si__ai-guide">
            <div className="si__ai-guide-header">
              <span className="si__ai-guide-title">📊 Reorder Decision Guide</span>
            </div>

            <div className="si__ai-top-row">
              <div className={`si__ai-urgency si__ai-urgency--${guide.urgency}`}>
                {({critical:'🚨',high:'⚠️',medium:'📦',low:'✓'} as any)[guide.urgency]}
                {' '}{guide.urgency.toUpperCase()} URGENCY
              </div>
              <div className={`si__ai-strategy si__ai-strategy--${guide.stockStrategy}`}>
                {({bulk:'📦 BUY BIG — Build large stock', minimum:'📉 MINIMUM — Just enough stock', skip:'🚫 SKIP — Do not reorder'} as any)[guide.stockStrategy]}
              </div>
            </div>

            <div className="si__ai-numbers">
              {guide.reorderQty > 0 && (
                <div className="si__ai-num-card">
                  <div className="si__ai-num-label">Reorder Qty</div>
                  <div className="si__ai-num-value" style={{ color: '#378ADD' }}>{guide.reorderQty} units</div>
                </div>
              )}
              {guide.targetBuyingPrice > 0 && (
                <div className="si__ai-num-card">
                  <div className="si__ai-num-label">Target Buy Price</div>
                  <div className="si__ai-num-value" style={{ color: '#7C3AED' }}>{guide.targetBuyingPrice} MAD</div>
                  {guide.savingsPerUnit > 0 && (
                    <div style={{ fontSize: '0.62rem', color: '#1D9E75', marginTop: 2 }}>
                      -{guide.savingsPerUnit} MAD vs current
                    </div>
                  )}
                </div>
              )}
              {guide.suggestedSellingPrice > 0 && (
                <div className="si__ai-num-card">
                  <div className="si__ai-num-label">Suggested Sell Price</div>
                  <div className="si__ai-num-value" style={{ color: '#1D9E75' }}>{guide.suggestedSellingPrice} MAD</div>
                </div>
              )}
              {guide.expectedMargin !== null && (
                <div className="si__ai-num-card">
                  <div className="si__ai-num-label">Expected Margin</div>
                  <div className="si__ai-num-value" style={{ color: guide.expectedMargin >= 25 ? '#1D9E75' : guide.expectedMargin >= 10 ? '#EF9F27' : '#E24B4A' }}>
                    {guide.expectedMargin}%
                  </div>
                </div>
              )}
              {guide.coverageDays > 0 && (
                <div className="si__ai-num-card">
                  <div className="si__ai-num-label">Stock Coverage</div>
                  <div className="si__ai-num-value" style={{ color: '#EF9F27' }}>{guide.coverageDays} days</div>
                </div>
              )}
            </div>

            <div className="si__ai-section">
              <div className="si__ai-section-title">💡 Reasoning</div>
              <div className="si__ai-reasoning">{guide.reasoning}</div>
            </div>

            <div className="si__ai-bottom-row">
              <div className="si__ai-section">
                <div className="si__ai-section-title">⚠ Risks to Watch</div>
                <ul className="si__ai-list si__ai-list--risk">
                  {guide.risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
              <div className="si__ai-section">
                <div className="si__ai-section-title">✅ Action Tips</div>
                <ul className="si__ai-list si__ai-list--tip">
                  {guide.tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* SKU breakdown table */}
        {g.skus.length > 0 && (
          <div className="si__sku-table-wrap">
            <div className="si__sku-table-title">
              SKU Breakdown — {g.skus.length} listing{g.skus.length > 1 ? 's' : ''}
              {topSku && g.skus.length > 1 && (
                <span style={{ marginLeft: '0.5rem', fontWeight: 400, color: '#6B7280' }}>
                  · {topSku.url
                      ? <a href={topSku.url} target="_blank" rel="noopener noreferrer">{topSku.Sku || topSku._id}</a>
                      : (topSku.Sku || topSku._id)
                    } carries{' '}
                  {groupTotal30 > 0 ? Math.round((topSku.totalOrders30d / groupTotal30) * 100) : 0}% of group sales
                </span>
              )}
            </div>
            <table className="si__sku-table">
              <thead>
                <tr>
                  <th>SKU / Name</th>
                  <th>Store</th>
                  <th>Orders 7d</th>
                  <th>Orders 30d</th>
                  {isAdmin && <th>Revenue 30d</th>}
                  <th>Share of Group</th>
                </tr>
              </thead>
              <tbody>
                {[...g.skus]
                  .sort((a, b) => b.totalOrders30d - a.totalOrders30d)
                  .map((sku, idx) => {
                    const share = groupTotal30 > 0 ? (sku.totalOrders30d / groupTotal30) * 100 : 0;
                    const isStar = idx === 0 && g.skus.length > 1 && share >= 50;
                    return (
                      <tr key={sku._id} className={isStar ? 'si__sku-star-row' : ''}>
                        <td>
                          <span className="si__sku-name">
                            {sku.url ? (
                              <a href={sku.url} target="_blank" rel="noopener noreferrer">{sku.name || sku.Sku || '—'}</a>
                            ) : (
                              sku.name || sku.Sku || '—'
                            )}
                            {isStar && <span className="si__sku-star-badge">★ STAR</span>}
                          </span>
                          <span className="si__sku-id">
                            {sku.url ? (
                              <a href={sku.url} target="_blank" rel="noopener noreferrer">{sku.Sku}</a>
                            ) : (
                              sku.Sku
                            )}
                          </span>
                        </td>
                        <td style={{ color: '#6B7280', fontSize: '0.72rem' }}>
                          {sku.storeName || '—'}
                        </td>
                        <td>
                          <span className="si__num">{sku.totalOrders7d}</span>
                          <span className="si__num-sub">{sku.delivered7d} delivered</span>
                        </td>
                        <td>
                          <span className="si__num">{sku.totalOrders30d}</span>
                          <span className="si__num-sub">{sku.delivered30d} delivered</span>
                        </td>
                        {isAdmin && (
                          <td>
                            <span className={`si__num${sku.revenue30d > 0 ? ' si__num--green' : ' si__num--muted'}`}>
                              {sku.revenue30d > 0 ? fmtMAD(sku.revenue30d) : '—'}
                            </span>
                          </td>
                        )}
                        <td>
                          <div className="si__sku-share-bar">
                            <div className="si__sku-share-track">
                              <div className="si__sku-share-fill" style={{ width: `${share}%` }} />
                            </div>
                            <span className="si__sku-share-pct">{Math.round(share)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="si">
      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <div className="si__topbar">
        <div className="si__title-block">
          <div className="si__title-icon">
            <Boxes size={14} color="#fff" strokeWidth={2} />
          </div>
          <div>
            <div className="si__title">Stock Intelligence Panel</div>
            <div className="si__subtitle">Real-time stock decisions · reorder alerts · AI recommendations</div>
          </div>
        </div>

        {stores.length > 0 && (
          <select
            className="si__select"
            value={storeId}
            onChange={e => setStoreId(e.target.value)}
          >
            <option value="0">All Stores</option>
            {stores.map((s: IStoreResponse) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        )}

        {lastUpdated && (
          <span className="si__last-updated">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}

        <button className="si__refresh-btn" onClick={fetchData} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'si__refresh-spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>

      </div>

      <div className="si__body">
        {/* loading / error (2026-10-13): never a silent empty page — on a phone it looked broken */}
        {loading && !groups.length && <div className="si__loadnote">⏳ Loading the products…</div>}
        {loadError && !loading && (
          <div className="si__loaderr">⚠️ The products could not be loaded: {loadError} <button onClick={fetchData}>Retry</button></div>
        )}

        {/* ── KPI Grid ───────────────────────────────────────────────────────── */}
        <div className="si__kpi-grid">
          {[
            ...(isAdmin ? [{
              icon: <DollarSign size={14} color="#378ADD" />,
              iconBg: '#EFF6FF', label: 'Total Stock Value',
              value: loading ? '—' : fmtMAD(kpis.totalStockValue),
              colorClass: 'si__kpi-value--blue',
              sub: `${groups.length} product groups`,
            }] : []),
            ...(isAdmin ? [{
              icon: <AlertTriangle size={14} color="#E24B4A" />,
              iconBg: '#FEF2F2', label: 'Dead Stock (locked cash)',
              value: loading ? '—' : fmtMAD(kpis.deadStockValue),
              colorClass: 'si__kpi-value--red',
              sub: `${kpis.deadStockCount} group${kpis.deadStockCount !== 1 ? 's' : ''} with 0 orders/30d`,
            }] : []),
            {
              icon: <Package size={14} color="#EF9F27" />,
              iconBg: '#FFFBEB', label: 'Groups at Stockout Risk',
              value: loading ? '—' : String(kpis.atRiskCount),
              colorClass: kpis.atRiskCount > 0 ? 'si__kpi-value--amber' : 'si__kpi-value--green',
              sub: 'coverage < 7 days',
            },
            {
              icon: <Zap size={14} color="#1D9E75" />,
              iconBg: '#F0FDF4', label: 'Winners / Fast-Moving',
              value: loading ? '—' : `${kpis.winnerCount} / ${kpis.fastMoving}`,
              colorClass: 'si__kpi-value--green',
              sub: 'winner · ≥2 orders/day',
            },
            ...(isAdmin ? [{
              icon: <ShoppingCart size={14} color="#7C3AED" />,
              iconBg: '#F5F3FF', label: 'Revenue (30 days)',
              value: loading ? '—' : fmtMAD(kpis.totalRevenue30),
              colorClass: 'si__kpi-value--violet',
              sub: 'delivered orders only',
            }] : []),
            ...(isAdmin ? [{
              icon: <TrendingUp size={14} color="#1D9E75" />,
              iconBg: '#F0FDF4', label: 'Net Profit (30 days)',
              value: loading ? '—' : fmtMAD(kpis.totalProfit30),
              colorClass: kpis.totalProfit30 >= 0 ? 'si__kpi-value--green' : 'si__kpi-value--red',
              sub: 'after all costs',
            }] : []),
            {
              icon: <BarChart3 size={14} color="#378ADD" />,
              iconBg: '#EFF6FF', label: 'Fast-Moving Groups',
              value: loading ? '—' : String(kpis.fastMoving),
              colorClass: 'si__kpi-value--blue',
              sub: '≥ 2 orders/day velocity',
            },
            {
              icon: <Target size={14} color="#EF9F27" />,
              iconBg: '#FFFBEB', label: 'Dead Stock Groups',
              value: loading ? '—' : String(kpis.deadStockCount),
              colorClass: kpis.deadStockCount > 0 ? 'si__kpi-value--amber' : 'si__kpi-value--green',
              sub: '0 orders in 30 days',
            },
          ].map((k, i) => (
            <div key={i} className="si__kpi-card">
              <div className="si__kpi-header">
                <div className="si__kpi-icon" style={{ background: k.iconBg }}>
                  {k.icon}
                </div>
                <span className="si__kpi-label">{k.label}</span>
              </div>
              <div className={`si__kpi-value ${k.colorClass}`}>{k.value}</div>
              <div className="si__kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Critical alerts ───────────────────────────────────────────────── */}
        {!loading && criticalAlerts.length > 0 && (
          <div className="si__alert-strip">
            <div className="si__alert-title">
              <AlertTriangle size={13} />
              🚨 CRITICAL — {criticalAlerts.length} Group{criticalAlerts.length > 1 ? 's' : ''} Stockout in &lt; 3 Days
            </div>
            <div className="si__alert-list">
              {criticalAlerts.map(g => (
                <div key={g._id} className="si__alert-item">
                  <div className="si__alert-dot" />
                  <span className="si__alert-name">{g.name || g._id}</span>
                  <span className="si__alert-vel">{fmt1(g.salesVelocity7d)}/day</span>
                  <span className="si__alert-days">
                    {g.stockCoverageDays < 1 ? '&lt;1d' : `${fmt1(g.stockCoverageDays)}d`} left
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Warnings ─────────────────────────────────────────────────────── */}
        {!loading && warnAlerts.length > 0 && (
          <div className="si__warn-strip">
            <div className="si__warn-title">
              <AlertTriangle size={12} />
              ⚠️ WARNING — {warnAlerts.length} Group{warnAlerts.length > 1 ? 's' : ''} with 3–7 Days of Stock
            </div>
            <div className="si__warn-list">
              {warnAlerts.map(g => (
                <span key={g._id} className="si__warn-chip">
                  {g.name || g._id} ({fmt1(g.stockCoverageDays)}d)
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Filter bar ───────────────────────────────────────────────────── */}
        <div className="si__filter-bar">
          <div className="si__search">
            <Search size={13} color="#9CA3AF" />
            <input
              placeholder="Search product group…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="si__filter-divider" />

          <div className="si__filter-group">
            <span className="si__filter-label">Decision</span>
            <select className="si__filter-select" value={filterDecision} onChange={e => setFilterDecision(e.target.value)}>
              <option value="all">All</option>
              <option value="winner">🔥 Winners</option>
              <option value="stock-risk">⚠️ Stock Risk</option>
              <option value="stock-warn">⚠️ Stock Warning</option>
              <option value="dead-stock">⛔ Dead Stock</option>
              <option value="near-dead">⛔ Near-Dead</option>
              <option value="price-up">💰 Price Up</option>
              <option value="price-down">📉 Price Down</option>
              <option value="stop-buying">❌ Stop Buying</option>
              <option value="test-more">🔍 Test More SKUs</option>
              <option value="watch">👁 Monitor</option>
            </select>
          </div>

          <div className="si__filter-group">
            <span className="si__filter-label">Risk</span>
            <select className="si__filter-select" value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
              <option value="all">All</option>
              <option value="critical">🔴 Critical (&lt;3d)</option>
              <option value="warning">🟠 Warning (&lt;7d)</option>
              <option value="safe">🟢 Safe (14d+)</option>
            </select>
          </div>

          <div className="si__filter-divider" />

          {['velocity', 'revenue', 'profit', 'margin', 'trend', 'coverage', 'orders30', 'stock'].map(f => (
            <button
              key={f}
              className={`si__sort-btn${sortBy === f ? ' si__sort-btn--active' : ''}`}
              onClick={() => handleSort(f)}
            >
              {f === 'velocity' ? 'Velocity' : f === 'revenue' ? 'Revenue' : f === 'profit' ? 'Profit' :
               f === 'margin' ? 'Margin' : f === 'trend' ? 'Trend' : f === 'coverage' ? 'Coverage' :
               f === 'orders30' ? 'Orders' : 'Stock'}
              {sortBy === f && <span style={{ opacity: 0.7 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>}
            </button>
          ))}

          <span className="si__count-badge">{filtered.length} groups</span>
        </div>

        {/* ── Main Intelligence Table ───────────────────────────────────────── */}
        <div className="si__table-wrap">
          <table className="si__table">
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <SortTh field="name_" label="Product Group" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortTh field="stock"    label="Stock / Coverage" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortTh field="velocity" label="Velocity"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortTh field="orders30" label="Orders 7d / 30d" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                {isAdmin && <SortTh field="revenue"  label="Revenue 30d" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />}
                {isAdmin && <SortTh field="profit"   label="Profit 30d" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />}
                {isAdmin && <SortTh field="margin"   label="Margin" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />}
                <SortTh field="trend"    label="Trend 7d" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th>AI Decision</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 7}>
                    <div className="si__empty">
                      <div className="si__empty-icon">📦</div>
                      <div className="si__empty-msg">No product groups match your filters</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(g => {
                  const decision   = getDecision(g);
                  const reorder    = getReorder(g);
                  const cc         = covClass(g.stockCoverageDays, g.salesVelocity7d > 0);
                  const isExpanded = expandedId === g._id;
                  const trendDir   = g.trend7d > 5 ? 'up' : g.trend7d < -5 ? 'down' : 'flat';

                  return (
                    <>
                      <tr
                        key={g._id}
                        className={`si__row${isExpanded ? ' si__row--expanded' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : g._id)}
                      >
                        {/* expand toggle */}
                        <td>
                          <div className="si__expand-btn">
                            {isExpanded
                              ? <ChevronDown size={12} color="#6B7280" />
                              : <ChevronRight size={12} color="#6B7280" />
                            }
                          </div>
                        </td>

                        {/* group name */}
                        <td>
                          <div className="si__group-name-cell">
                            <div>
                              <div className="si__group-name">{g.name || '(unnamed)'}</div>
                              {reorder && (
                                <div style={{ marginTop: '0.2rem' }}>
                                  <span className={`si__reorder si__reorder--${reorder.urgency}`}>
                                    ↺ Reorder {reorder.reorderQty}u
                                  </span>
                                </div>
                              )}
                            </div>
                            <span className="si__sku-count">{g.totalSkus} SKU{g.totalSkus !== 1 ? 's' : ''}</span>
                          </div>
                        </td>

                        {/* stock / coverage */}
                        <td>
                          <div className="si__cov-cell">
                            <div className="si__cov-top">
                              <span className={`si__cov-days si__cov-days--${cc}`}>
                                {g.stockCoverageDays >= 9999
                                  ? '∞'
                                  : g.salesVelocity7d === 0
                                  ? '—'
                                  : `${fmt1(g.stockCoverageDays)}d`}
                              </span>
                              {g.salesVelocity7d > 0 && g.stockCoverageDays < 9999 && (
                                <span className="si__cov-unit">coverage</span>
                              )}
                            </div>
                            {g.salesVelocity7d > 0 && (
                              <div className="si__cov-track">
                                <div
                                  className={`si__cov-fill si__cov-fill--${cc}`}
                                  style={{ width: `${covPct(g.stockCoverageDays)}%` }}
                                />
                              </div>
                            )}
                            <div className="si__stock-qty">{g.stockAvailable} in stock</div>
                          </div>
                        </td>

                        {/* velocity */}
                        <td>
                          <div className="si__vel-cell">
                            <span className="si__vel-value">
                              {g.salesVelocity7d > 0 ? fmt1(g.salesVelocity7d) : '0'}
                            </span>
                            <span className="si__vel-label">orders/day</span>
                            <VelDots vel={g.salesVelocity7d} />
                          </div>
                        </td>

                        {/* orders 7d / 30d */}
                        <td>
                          <span className="si__num">{g.totalOrders7d}</span>
                          <span className="si__num-sub">{g.totalOrders30d} in 30d</span>
                        </td>

                        {/* revenue 30d — admin only */}
                        {isAdmin && (
                          <td>
                            <span className={`si__num${g.revenue30d > 0 ? ' si__num--green' : ' si__num--muted'}`}>
                              {g.revenue30d > 0 ? fmtMAD(g.revenue30d) : '—'}
                            </span>
                            {g.revenue7d > 0 && (
                              <span className="si__num-sub">{fmtMAD(g.revenue7d)} (7d)</span>
                            )}
                          </td>
                        )}

                        {/* profit 30d — admin only */}
                        {isAdmin && (
                          <td>
                            <span className={`si__num${g.netProfit30d > 0 ? ' si__num--green' : g.netProfit30d < 0 ? ' si__num--red' : ' si__num--muted'}`}>
                              {g.delivered30d > 0 ? fmtMAD(g.netProfit30d) : '—'}
                            </span>
                            {g.delivered30d > 0 && (
                              <span className="si__num-sub">
                                {fmtMAD(g.netProfit30d / g.delivered30d)}/unit
                              </span>
                            )}
                          </td>
                        )}

                        {/* margin — admin only */}
                        {isAdmin && (
                          <td>
                            <div className="si__pct-bar">
                              <span className={`si__pct-value${g.margin30d >= 25 ? ' si__num--green' : g.margin30d >= 10 ? ' si__num--amber' : ' si__num--red'}`}>
                                {g.revenue30d > 0 ? `${fmt1(g.margin30d)}%` : '—'}
                              </span>
                              {g.revenue30d > 0 && (
                                <div className="si__pct-track">
                                  <div
                                    className="si__pct-fill"
                                    style={{
                                      width: `${clamp(g.margin30d, 0, 100)}%`,
                                      background: g.margin30d >= 25 ? '#1D9E75' : g.margin30d >= 10 ? '#EF9F27' : '#E24B4A',
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                        )}

                        {/* trend */}
                        <td>
                          {g.totalOrders7dPrev === 0 && g.totalOrders7d === 0 ? (
                            <span className="si__num--muted" style={{ fontSize: '0.75rem' }}>—</span>
                          ) : (
                            <span className={`si__trend si__trend--${trendDir}`}>
                              {trendDir === 'up'   ? <ArrowUpRight size={11} /> :
                               trendDir === 'down' ? <ArrowDownRight size={11} /> : null}
                              {g.totalOrders7dPrev === 0 && g.totalOrders7d > 0 ? 'NEW' : fmtPct(g.trend7d)}
                            </span>
                          )}
                        </td>

                        {/* decision */}
                        <td>
                          <span className={`si__decision ${decision.colorClass}`}>
                            {decision.emoji} {decision.label}
                          </span>
                        </td>
                      </tr>

                      {/* ── Expanded SKU panel ── */}
                      {isExpanded && (
                        <tr key={`${g._id}-exp`} className="si__expanded-row">
                          <td colSpan={isAdmin ? 10 : 7}>
                            <SkuPanel g={g} />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Bottom panels: Opportunities + Dead Stock ─────────────────────── */}
        {!loading && (opportunities.length > 0 || deadStock.length > 0) && (
          <div className="si__bottom-grid">
            {/* Opportunity Detection */}
            <div className="si__panel">
              <div className="si__panel-header">
                <Zap size={13} color="#1D9E75" />
                <span className="si__panel-title">Opportunity Detection</span>
                <span className="si__panel-count">{opportunities.length}</span>
              </div>
              <div className="si__panel-body">
                {opportunities.length === 0 ? (
                  <div style={{ padding: '1rem', fontSize: '0.78rem', color: '#9CA3AF' }}>
                    No strong opportunities detected right now
                  </div>
                ) : (
                  opportunities.map(g => {
                    const tag = g.trend7d >= 40
                      ? { label: `+${Math.round(g.trend7d)}% trend`, cls: 'si__opp-tag--grow' }
                      : g.margin30d >= 40
                      ? { label: `${Math.round(g.margin30d)}% margin`, cls: 'si__opp-tag--margin' }
                      : { label: `${fmt1(g.salesVelocity7d)}/day`, cls: 'si__opp-tag--winner' };
                    return (
                      <div key={g._id} className="si__panel-row">
                        <span className="si__panel-row-name">{g.name || '—'}</span>
                        <span className="si__panel-row-detail">{fmt1(g.salesVelocity7d)}/day</span>
                        <span className={`si__opp-tag ${tag.cls}`}>{tag.label}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Dead Stock */}
            <div className="si__panel">
              <div className="si__panel-header">
                <Package size={13} color="#E24B4A" />
                <span className="si__panel-title">Dead Stock — Cash at Risk</span>
                <span className="si__panel-count">{deadStock.length}</span>
              </div>
              <div className="si__panel-body">
                {deadStock.length === 0 ? (
                  <div style={{ padding: '1rem', fontSize: '0.78rem', color: '#9CA3AF' }}>
                    No dead stock detected
                  </div>
                ) : (
                  deadStock.map(g => (
                    <div key={g._id} className="si__panel-row">
                      <span className="si__panel-row-name">{g.name || '—'}</span>
                      <span className="si__panel-row-detail">{g.stockAvailable} units</span>
                      {isAdmin && <span className="si__dead-value">{fmtMAD(g.stockAvailable * g.buyingRate)}</span>}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockIntelligencePage;
